import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, PackageCheck, QrCode, UserRound } from 'lucide-react';
import TomSelect from 'tom-select';

export const HANDOVER_MODES = {
  QR: 'qr',
  MANUAL: 'manual',
  UNATTENDED: 'unattended',
};

const readEmployees = () => {
  try {
    const value = JSON.parse(localStorage.getItem('karyawan_pengganti_list') || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const compressPhoto = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const image = new Image();
    image.onerror = reject;
    image.onload = () => {
      const maxSize = 1280;
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.72));
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const choices = [
  {
    key: HANDOVER_MODES.QR,
    icon: QrCode,
    title: 'Bertemu + Punya Aplikasi',
    description: 'Penerima memindai barcode dari aplikasi.',
  },
  {
    key: HANDOVER_MODES.UNATTENDED,
    icon: Camera,
    title: 'Tidak Bertemu',
    description: 'Wajib foto bukti dan isi lokasi/keterangan.',
  },
];

export default function HandoverDeliveryModal({
  open,
  itemCount = 1,
  defaultRecipientName = '',
  onClose,
  onSubmit,
}) {
  const [employees, setEmployees] = useState(readEmployees);

  useEffect(() => {
    if (!open) return;
    const current = readEmployees();
    if (current && current.length > 0) {
      setEmployees(current);
    } else {
      fetch('https://jne-cilacap.com/slip/public/api/riwayat-absen/108')
        .then(res => res.json())
        .then(json => {
          if (json.success && Array.isArray(json.karyawan_pengganti_list)) {
            setEmployees(json.karyawan_pengganti_list);
            try {
              localStorage.setItem('karyawan_pengganti_list', JSON.stringify(json.karyawan_pengganti_list));
            } catch {}
          }
        })
        .catch(() => {});
    }
  }, [open]);
  const [mode, setMode] = useState('');
  const [receiverId, setReceiverId] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const unattendedReceiverRef = useRef(null);
  const unattendedSelectRef = useRef(null);

  useEffect(() => {
    if (!open || mode !== HANDOVER_MODES.UNATTENDED || !unattendedReceiverRef.current) return undefined;

    unattendedSelectRef.current?.destroy();
    const commitReceiver = (id) => {
      const selected = employees.find((entry) => String(entry.id) === String(id));
      const manualName = String(id || '').startsWith('external:')
        ? String(id).slice('external:'.length)
        : '';
      setReceiverId(id);
      setReceiverName(selected?.nama || manualName);
    };
    unattendedSelectRef.current = new TomSelect(unattendedReceiverRef.current, {
      valueField: 'id',
      labelField: 'nama',
      searchField: ['nama', 'kantor', 'jabatan'],
      options: employees,
      placeholder: 'Cari atau ketik nama penerima...',
      create: (input) => ({
        id: `external:${input.trim()}`,
        nama: input.trim(),
        external: true,
      }),
      createOnBlur: true,
      closeAfterSelect: true,
      selectOnTab: true,
      persist: false,
      maxItems: 1,
      render: {
        option: (data, escape) => `
          <div style="padding:7px 9px">
            <div style="font-weight:700;color:#1e293b">${escape(data.nama)}</div>
            ${data.external
              ? '<div style="font-size:11px;color:#2563eb">Penerima manual · tidak terdaftar di SIHC</div>'
              : `<div style="font-size:11px;color:#64748b">${escape([data.jabatan, data.kantor].filter(Boolean).join(' · '))}</div>`}
          </div>`,
        item: (data, escape) => `<div>${escape(data.nama)}</div>`,
        option_create: (data, escape) => `
          <div class="create" style="padding:9px;color:#2563eb">
            Gunakan nama manual: <strong>${escape(data.input)}</strong>
          </div>`,
      },
      onItemAdd: (id) => commitReceiver(id),
      onChange: (id) => commitReceiver(id),
    });

    return () => {
      unattendedSelectRef.current?.destroy();
      unattendedSelectRef.current = null;
    };
  }, [open, mode, employees]);

  if (!open) return null;

  const resetAndClose = () => {
    setMode('');
    setReceiverId('');
    setReceiverName('');
    setNotes('');
    setPhoto('');
    onClose?.();
  };

  const handlePhoto = async (file) => {
    if (!file) return;
    try {
      setPhoto(await compressPhoto(file));
    } catch {
      alert('Foto tidak dapat diproses. Silakan ambil foto kembali.');
    }
  };

  const isValid = mode === HANDOVER_MODES.QR
    || (mode === HANDOVER_MODES.MANUAL && receiverName.trim())
    || (mode === HANDOVER_MODES.UNATTENDED && receiverId && notes.trim() && photo);

  const submit = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit?.({
        mode,
        receiver_id: receiverId,
        receiver_name: receiverName.trim(),
        notes: notes.trim(),
        photo,
      });
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(15,23,42,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 24, padding: 22, boxSizing: 'border-box' }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <PackageCheck size={34} color="#2563eb" />
          <h3 style={{ margin: '8px 0 4px', color: '#1e293b' }}>Pilih Cara Serah Terima</h3>
          <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>{itemCount} item · Tujuan akhir: {defaultRecipientName || '-'}</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {choices.map(({ key, icon: Icon, title, description }) => (
            <button key={key} type="button" onClick={() => setMode(key)} style={{ display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left', padding: 14, borderRadius: 14, border: `2px solid ${mode === key ? '#2563eb' : '#e2e8f0'}`, background: mode === key ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
              <Icon size={22} color={mode === key ? '#2563eb' : '#64748b'} />
              <span>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 13, color: '#1e293b' }}>{title}</span>
                <span style={{ display: 'block', fontSize: 11, color: '#64748b', marginTop: 2 }}>{description}</span>
              </span>
            </button>
          ))}
        </div>

        {mode === HANDOVER_MODES.MANUAL && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Nama penerima aktual *</label>
            <input value={receiverName} onChange={(event) => { setReceiverName(event.target.value); setReceiverId(''); }} placeholder="Masukkan nama lengkap penerima" style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 12, border: '1.5px solid #cbd5e1' }} />
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Keterangan tambahan (opsional)" rows={2} style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 12, border: '1.5px solid #cbd5e1', marginTop: 10, resize: 'vertical' }} />
          </div>
        )}

        {mode === HANDOVER_MODES.UNATTENDED && (
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Ditinggalkan untuk *</label>
            <select ref={unattendedReceiverRef} defaultValue="" style={{ width: '100%' }}>
              <option value="">Cari atau ketik nama penerima...</option>
            </select>
            <div style={{ marginTop: 6, fontSize: 10, color: '#64748b' }}>
              Jika penerima tidak terdaftar di SIHC, ketik nama lengkap lalu pilih “Gunakan nama manual”.
            </div>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Contoh: Diletakkan di meja penerima, sebelah monitor." rows={3} style={{ width: '100%', boxSizing: 'border-box', padding: 12, borderRadius: 12, border: '1.5px solid #cbd5e1', marginTop: 10, resize: 'vertical' }} />
            <input id="handover-proof-photo" type="file" accept="image/*" capture="environment" onChange={(event) => handlePhoto(event.target.files?.[0])} style={{ display: 'none' }} />
            <label htmlFor="handover-proof-photo" style={{ marginTop: 10, minHeight: 76, borderRadius: 12, border: '2px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer', color: '#64748b', fontSize: 12 }}>
              {photo ? <img src={photo} alt="Bukti penyerahan" style={{ width: '100%', maxHeight: 180, objectFit: 'cover' }} /> : <><Camera size={17} style={{ marginRight: 7 }} /> Ambil Foto Bukti *</>}
            </label>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={resetAndClose} style={{ flex: 1, padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 700 }}>Batal</button>
          <button type="button" disabled={!isValid || isSubmitting} onClick={submit} style={{ flex: 1.5, padding: 12, borderRadius: 12, border: 'none', background: isValid ? '#2563eb' : '#cbd5e1', color: '#fff', fontWeight: 700 }}>
            {isSubmitting ? 'Menyimpan...' : mode === HANDOVER_MODES.QR ? 'Tampilkan Barcode' : mode === HANDOVER_MODES.MANUAL ? 'Terima & Catat' : 'Simpan Bukti'}
          </button>
        </div>
      </div>
    </div>
  );
}
